import { Activity, CSSProperties, memo, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'underscore';

import styles from './custom-select.module.css';

import { TReactSelectOption } from '../react-select/custom-react-select.types';
import CustomSelectUnfocusedItems from './components/custom-select-unfocused-items';
import CustomSelectMenu from './components/custom-select-menu';
import CustomSelectHeader from './components/custom-select-header';
import CustomSelectFocusedItems from './components/custom-select-focused-items';
import CustomSelectInput from './components/custom-select-input';
import selectKeyDownCapture from './helpers/select-keydown-capture';
import useResetAttributes from './helpers/use-reset-attributes';
import useScrollResets from './helpers/use-scroll-resets';
import useFilteredOptions from './helpers/use-filtered-options';
import FakeBorder from './components/fake-border';
import clearHighlightedSelection from '@/utils/clear-highlighted-selection';
import { TCustomSelectProps } from './custom-select.types';
import Container from '@ui/container/container';
import useClickAwayListener from '@/utils/use-click-away-listener';
import { mergeRefs } from '@/utils/merge-refs';

const CustomSelect = memo(
  ({
    id,
    placeholder,
    options: allOptions,
    onFocusClosed,
    defaultOptions,
    controlledOptions,
    onFocusChange,
    RenderFocusedLabelContent,
    RenderUnfocusedLabelContent,
    RenderMenuOptionContent,
    RenderNoOptionsContent,
    lockActiveState: lockActiveStateProps = false,
    onAddNewSelect,
    labelHeight = '42px',
    RenderHeaderLabelContent,
    onMenuClose,
    onMenuOpen,
    hideHeader,
    onChange,
    filterSelected = true,
    autoFocus,
    maskOverflowColor,
    inputRef: propsInputRef,
    containerRef: propsContainerRef,
    disabled,
    selectFirstSearchOptionOnEnter = true,
    invisibleTriggerRef,
    menuOptionSelectedIndicatorLabel,
    clearInputOnEnter = true,
    clearInputOnMouseSelect = true,
    dynamicMenuPlacement = false,
  }: TCustomSelectProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectRef = useRef<HTMLDivElement | null>(null);
    const selectedOptionsRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const hasTriggeredAutoFocus = useRef(false);

    const [searchValue, setSearchValue] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<TReactSelectOption[]>(
      controlledOptions || defaultOptions || []
    );

    useEffect(() => {
      if (autoFocus && !hasTriggeredAutoFocus.current) {
        hasTriggeredAutoFocus.current = true;
        inputRef?.current?.focus();
      }
    }, [autoFocus]);

    useEffect(() => {
      if (controlledOptions) {
        setSelectedOptions(controlledOptions);
      }
    }, [controlledOptions]);

    // Use refs to store the latest callbacks to avoid infinite loops
    // when parent passes a new function reference on each render
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const onFocusClosedRef = useRef(onFocusClosed);
    onFocusClosedRef.current = onFocusClosed;

    const onFocusChangeRef = useRef(onFocusChange);
    onFocusChangeRef.current = onFocusChange;

    const onMenuCloseRef = useRef(onMenuClose);
    onMenuCloseRef.current = onMenuClose;

    const onMenuOpenRef = useRef(onMenuOpen);
    onMenuOpenRef.current = onMenuOpen;

    // Create a stable debounced function
    // otherwise adding/removing too fast (like holding down the enter/backspace keys) creates infinite loops
    const debouncedOnChange = useMemo(
      () =>
        debounce((options: TReactSelectOption[]) => {
          onChangeRef.current?.(options);
        }, 150),
      []
    );

    useEffect(() => {
      debouncedOnChange(selectedOptions);
    }, [debouncedOnChange, selectedOptions]);

    const selectableOptions = useFilteredOptions({
      searchValue,
      selectedOptions,
      allOptions,
      filterSelected,
    });

    const hasActivatedAtleastOnceRef = useRef(false);
    useEffect(() => {
      if (isFocused || lockActiveStateProps || menuOpen) {
        hasActivatedAtleastOnceRef.current = true;
      }
    }, [isFocused, lockActiveStateProps, menuOpen]);

    const hasActivatedAtleastOnce = hasActivatedAtleastOnceRef.current;

    const lockActiveState = menuOpen || isFocused || lockActiveStateProps;

    useEffect(() => {
      if (menuOpen) onMenuOpenRef.current?.();
      if (!menuOpen && hasActivatedAtleastOnce) {
        onMenuCloseRef.current?.();
      }
    }, [hasActivatedAtleastOnce, menuOpen]);

    useEffect(() => {
      if (!lockActiveStateProps && !isFocused && hasActivatedAtleastOnce) {
        onFocusClosedRef.current?.(selectedOptions);
      }
    }, [lockActiveStateProps, isFocused, selectedOptions, hasActivatedAtleastOnce]);

    useEffect(() => {
      onFocusChangeRef.current?.(lockActiveState);
    }, [lockActiveState]);

    useScrollResets({
      selectRef,
      inputRef,
      setSearchValue,
      lockActiveState,
    });

    useResetAttributes({
      lockActiveStateProps,
      lockActiveState,
      menuOpen,
      menuRef,
      isFocused,
      selectedOptionsRef,
      setMenuOpen,
    });

    const handleToggleMenu = () => {
      if (lockActiveStateProps || !lockActiveState) return;
      setMenuOpen((prev) => !prev);
    };

    const handleRemoveClick = (option: string | number) => (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setSelectedOptions((prev) => prev.filter((so) => so.value.toString() !== option.toString()));
    };

    const handlePreventDefaultClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
    };

    useClickAwayListener({
      ref: containerRef,
      open: true,
      onPointerDownInside: () => {
        if (lockActiveStateProps || disabled || lockActiveState) return;

        setIsFocused(true);
        setMenuOpen((prev) => !prev);
        inputRef?.current?.click();
        inputRef?.current?.focus();
      },
      onPointerDownOutside: (event) => {
        if (lockActiveStateProps || disabled) return;
        const target = event.target instanceof HTMLElement ? event.target : null;
        if (!target) return;
        // menu is in a portal, so we need to check manually
        if (menuRef.current?.contains(target) || menuRef.current === target) {
          return;
        }
        setIsFocused(false);
        setMenuOpen(false);
      },
    });

    return (
      <Container
        className={`position-relative ${lockActiveState ? 'overflow-hidden' : ''}`}
        background="inherit"
        inert={disabled}
        ref={mergeRefs(containerRef, propsContainerRef)}
      >
        <span
          ref={invisibleTriggerRef}
          style={{ opacity: 0, visibility: 'hidden', height: 0, width: 0, position: 'absolute' }}
          onClick={() => {
            setMenuOpen(true);
            setIsFocused(true);
          }}
        />
        <div
          data-component="CustomSelect"
          ref={selectRef}
          role="combobox"
          aria-expanded={menuOpen}
          aria-label="Select options"
          className={`${styles.select} ${lockActiveState ? styles['is-focused'] : ''} ${selectedOptions?.length ? styles['has-value'] : ''}`}
          data-select-focused={lockActiveState}
          onKeyDownCapture={selectKeyDownCapture({
            lockActiveStateProps,
            lockActiveState,
            menuOpen,
            setMenuOpen,
            selectedOptionsRef,
            menuRef,
            selectRef,
            inputRef,
            selectedOptions,
            setSelectedOptions,
            onAddNewSelect,
            selectableOptions,
            allOptions,
            selectFirstSearchOptionOnEnter,
            clearInputOnEnter,
            setSearchValue,
          })}
          onPointerDownCapture={() => {
            // because we're prevent defaults in a lot of places, when the page has highlighted text
            // this entire select feature requires two clicks to get into it: one to remove highlight text, then one to enter
            // so we first need to clear all highlighted text to avoid that issue.
            // but only if we're not already "active", or the input itself won't be selectable
            if (!lockActiveState) {
              clearHighlightedSelection();
            }
          }}
        >
          <Activity mode={hideHeader ? 'hidden' : 'visible'}>
            <CustomSelectHeader
              handlePreventDefaultClick={handlePreventDefaultClick}
              RenderHeaderLabelContent={RenderHeaderLabelContent}
              isFocused={isFocused}
              inputRef={inputRef}
              handleToggleMenu={handleToggleMenu}
              selectedOptionsCount={selectedOptions.length}
            />
          </Activity>

          <label
            style={{ '--custom-select-control-height': labelHeight } as CSSProperties}
            htmlFor={id}
            role="presentation"
            className={`${styles.label} ${lockActiveState ? styles['lock-active-state'] : ''}`}
            onClick={handleToggleMenu} /* this lets child element clicks */
            onPointerDown={handlePreventDefaultClick}
            onPointerUp={(e) => {
              if (e.target instanceof HTMLLabelElement) {
                handleToggleMenu(); /* this enures we can can still "click" this component itself */
              }
            }}
          >
            <CustomSelectFocusedItems
              handlePreventDefaultClick={handlePreventDefaultClick}
              handleToggleMenu={handleToggleMenu}
              selectedOptions={selectedOptions}
              selectedOptionsRef={selectedOptionsRef}
              RenderFocusedLabelContent={RenderFocusedLabelContent}
              lockActiveState={lockActiveState}
              handleRemoveClick={handleRemoveClick}
            />

            <CustomSelectUnfocusedItems
              RenderUnfocusedLabelContent={RenderUnfocusedLabelContent}
              selectedOptions={selectedOptions}
              selectRef={selectRef}
              lockActiveState={lockActiveState}
            />

            <CustomSelectInput
              inputRef={inputRef}
              menuRef={menuRef}
              propsInputRef={propsInputRef}
              selectedOptionsRef={selectedOptionsRef}
              id={id}
              lockActiveState={lockActiveState}
              hasSelectedOptions={!!selectedOptions.length}
              placeholder={placeholder}
              setIsFocused={setIsFocused}
              setMenuOpen={setMenuOpen}
              setSearchValue={setSearchValue}
              disabled={disabled}
            />

            <CustomSelectMenu
              menuOpen={menuOpen}
              menuRef={menuRef}
              selectRef={selectRef}
              dynamicMenuPlacement={dynamicMenuPlacement}
              handlePreventDefaultClick={handlePreventDefaultClick}
              selectableOptions={selectableOptions}
              setSelectedOptions={setSelectedOptions}
              onAddNewSelect={onAddNewSelect}
              inputRef={inputRef}
              RenderMenuOptionContent={RenderMenuOptionContent}
              RenderNoOptionsContent={RenderNoOptionsContent}
              selectedOptions={selectedOptions}
              menuOptionSelectedIndicatorLabel={menuOptionSelectedIndicatorLabel}
              onSelectOption={({ status }) => {
                if (clearInputOnMouseSelect && status === 'selected') {
                  setSearchValue('');
                  if (inputRef?.current) {
                    inputRef.current.value = '';
                  }
                  // scroll to top of menu
                  menuRef?.current?.scrollTo({ top: 0 });
                }
              }}
            />
          </label>
        </div>

        <FakeBorder lockActiveState={lockActiveState} menuOpen={menuOpen} maskOverflowColor={maskOverflowColor} />
      </Container>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
